import { Box, Divider, Tooltip } from '@mui/material';
import { useState } from 'react';
import { Button } from '@sweep-io/sweep-design';
import { useDispatch, useSelector } from 'react-redux';
import { selectCrmOrgs } from '../../environments/environmentsReducer';
import { UNTITLED_FUNNEL_MAP_NAME } from '../../../../constants';
import DeployDialog from '../../funnel-map-flow/dialogs/deploy/DeployDialog';
import DeploymentChangesDialog from '../../funnel-map-flow/dialogs/deployment-changes/DeploymentChangesDialog';
import { InvitationsDialog } from '../../funnel-map-flow/dialogs/invitations/InvitationsDialog';
import { RestoreDialog } from '../../funnel-map-flow/dialogs/restore/RestoreDialog';
import { useFunnelMapReadOnly } from '../../funnel-map-flow/read-only/useFunnelMapReadOnly';
import RequestChanges from '../../funnel-map-flow/request-changes/RequestChanges';
import { AutoSave } from '../../funnel-map-flow/auto-save/AutoSave';
import { useConfigurationCanvas } from '../../configuration-canvas/public/useConfigurationCanvas';
import usePaywall from '../../../dashboard/paywall/usePaywall';
import CanvasDeployPaywallBlock from './CanvasDeployPaywallBlock';
import { usePageHelpers } from './usePageHelpers';
import { useCanDeployOrRequestToAnyOrg } from '../../../common/permissions/useCanDeployOrRequestToAnyOrg';
import { useFunnelMapPage } from '../../funnel-map-flow/useFunnelMapPage';
import { useHandleCommentsAndAiFirst } from './left-menu/useHandleCommentsAndAiFirst';
import AgentsHomeButton from '../../../aiAgentCenter/AgentsHomeButton';
import AiProcessAgentCreateButton from '../../../aiAgentCenter/AiProcessAgentCreateButton';
import useAgentsHome from '../../../aiAgentCenter/useAgentsHome';
import useUnitedCanvasMediaQueries from './useUnitedCanvasMediaQueries';
import { useProcessMining } from './useProcessMining';
import { selectIsPaywallDialogOpen } from '../../../../reducers/canvasPageStateReducer';
import { setIsPaywallDialogOpen } from '../../../../reducers/canvasPageStateReducer';

export const CanvasUnitedRightMenu = () => {
  const { onRestoreCallback, restoreFunnelName, versionName, funnelName } = useFunnelMapReadOnly();
  const { canvasCrmOrgId, canvasFunnelMap: funnelMap } = useConfigurationCanvas();
  const { funnelId, snapshotId } = usePageHelpers();
  const { onAiAgentCenterClick, isFunnelMapEmpty } = useFunnelMapPage();
  const handleCommentsAndAiFirst = useHandleCommentsAndAiFirst();
  const lastUpdatedAt = funnelMap.updatedAt;
  const { isCreateAgentActive } = useAgentsHome();
  const { isLoading: isProcessMiningLoading } = useProcessMining(funnelMap.id || '');

  const [isRestorePopupOpen, setIsRestorePopupOpen] = useState(false);
  const [isDeployDialogOpen, setIsDeployDialogOpen] = useState(false);
  const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false);

  const { isBigScreen, isMediumScreen, isSmallScreen } = useUnitedCanvasMediaQueries();
  const { canDeployOrRequestToAnyOrg } = useCanDeployOrRequestToAnyOrg();

  const orgs = useSelector(selectCrmOrgs);

  const { isPaywallActive } = usePaywall();
  const isPaywallDialogOpen = useSelector(selectIsPaywallDialogOpen);
  const dispatch = useDispatch();

  const onDeployClick = () => {
    if (isPaywallActive) {
      dispatch(setIsPaywallDialogOpen(true));
    } else {
      setIsDeployDialogOpen(true);
    }
  };

  const renderDeployButton = () => {
    let tooltipText = undefined;
    if (isFunnelMapEmpty) {
      tooltipText = 'In order to deploy you need to add at least one funnel';
    }

    if (orgs.length === 0) {
      tooltipText = 'In order to deploy you need to connect at least one Salesforce environment';
    }

    if (!canDeployOrRequestToAnyOrg()) {
      tooltipText = `You don't have permissions to deploy`;
    }

    return (
      <Tooltip title={tooltipText}>
        <span>
          <Button
            size="small"
            onClick={onDeployClick}
            disabled={Boolean(tooltipText) || isCreateAgentActive || isProcessMiningLoading}
            endIconName="SweepSolid"
          >
            Deploy
          </Button>
        </span>
      </Tooltip>
    );
  };

  const menuContentEdit = (
    <>
      {lastUpdatedAt && <AutoSave funnelMap={funnelMap} isButtonHidden={!isBigScreen} />}

      {isBigScreen && (
        <InvitationButton
          openInvitationDialog={() => setIsInvitationDialogOpen(true)}
          disabled={isCreateAgentActive || isProcessMiningLoading}
        />
      )}

      <RequestChanges funnelIds={Object.keys(funnelMap.funnelsData)} />
      {renderDeployButton()}

      {isPaywallDialogOpen && (
        <CanvasDeployPaywallBlock closeDialog={() => setIsPaywallDialogOpen(false)} />
      )}
    </>
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
        data-testid="funnel-flow-right-menu"
      >
        <AiProcessAgentCreateButton asIcon={isSmallScreen} disabled={isProcessMiningLoading} />

        <AgentsHomeButton
          onClick={() => handleCommentsAndAiFirst(onAiAgentCenterClick, true)}
          variant="system"
          disabled={isCreateAgentActive || isProcessMiningLoading}
          asIcon={!isMediumScreen}
        />
        <Divider orientation="vertical" variant="middle" flexItem />
        {menuContentEdit}
      </Box>

      {isDeployDialogOpen && (
        <DeployDialog
          open
          setIsOpen={setIsDeployDialogOpen}
          initialCrmOrgId={canvasCrmOrgId}
          funnels={Object.values(funnelMap.funnelsData)}
          funnelMapId={funnelMap.id}
        />
      )}

      <DeploymentChangesDialog />

      {isRestorePopupOpen && lastUpdatedAt && (
        <RestoreDialog
          funnelName={restoreFunnelName}
          onConfirm={() =>
            funnelId &&
            funnelName &&
            snapshotId &&
            onRestoreCallback({ funnelId, funnelName, snapshotId })
          }
          closeDialog={() => setIsRestorePopupOpen(false)}
          versionName={versionName}
          versionDate={lastUpdatedAt}
        />
      )}

      {isInvitationDialogOpen && (
        <InvitationsDialog
          funnelMapId={funnelMap.id}
          funnelMapName={funnelMap.name ?? UNTITLED_FUNNEL_MAP_NAME}
          isOpen={isInvitationDialogOpen}
          onClose={() => setIsInvitationDialogOpen(false)}
        />
      )}
    </>
  );
};

const InvitationButton = ({
  openInvitationDialog,
  disabled,
}: {
  openInvitationDialog: () => void;
  disabled: boolean;
}) => (
  <Button size="small" variant="system" onClick={openInvitationDialog} disabled={disabled}>
    Share
  </Button>
);
